import { useState, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, Upload, Download, Pencil, Loader2, Link2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, API } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { PageHeader } from "@/components/PageHeader";
import { DivisionTag } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { ErpCodesTable } from "@/components/ErpCodesTable";

const DIVS = [{ id: "univicoustic", n: "Univicoustic" }];

function ProductDialog({ open, onOpenChange, division, initial, onSaved }) {
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  useEffect(() => { if (open) setF(initial || { unit: "unit", base_price: 0 }); }, [open, initial]);
  const save = async () => {
    if (!f.name || !f.category) { toast.error("Name and category required"); return; }
    setSaving(true);
    const body = { name: f.name, category: f.category, sub_category: f.sub_category || "", model_number: f.model_number || "", base_price: Number(f.base_price || 0), unit: f.unit || "unit", erp_product_code: f.erp_product_code || "", specifications: f.specifications || {} };
    try {
      if (initial?.id) await api.put(`/products/${initial.id}`, body);
      else await api.post("/products", { ...body, division_id: division });
      toast.success("Saved"); onOpenChange(false); setF({}); onSaved?.();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setF({}); }}>
      <DialogContent data-testid="product-dialog">
        <DialogHeader><DialogTitle className="font-heading">{initial?.id ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1.5"><Label className="text-xs">Name *</Label><Input value={f.name || ""} onChange={(e) => set("name", e.target.value)} className="h-9" data-testid="product-name" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Category *</Label><Input value={f.category || ""} onChange={(e) => set("category", e.target.value)} className="h-9" data-testid="product-category" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Sub-category</Label><Input value={f.sub_category || ""} onChange={(e) => set("sub_category", e.target.value)} className="h-9" data-testid="product-subcategory" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Model Number</Label><Input value={f.model_number || ""} onChange={(e) => set("model_number", e.target.value)} className="h-9" data-testid="product-model" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Unit</Label><Input value={f.unit || ""} onChange={(e) => set("unit", e.target.value)} className="h-9" data-testid="product-unit" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Base Price (INR)</Label><Input type="number" value={f.base_price ?? ""} onChange={(e) => set("base_price", e.target.value)} className="h-9" data-testid="product-price" /></div>
          <div className="col-span-2 space-y-1.5"><Label className="text-xs">Milestone ERP Product Code</Label><Input value={f.erp_product_code || ""} onChange={(e) => set("erp_product_code", e.target.value)} className="h-9 font-mono" placeholder="e.g. MS-1023" data-testid="product-erp-code" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="product-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />}Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Products() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const qc = useQueryClient();
  const fileRef = useRef();
  const erpFileRef = useRef();
  const [division] = useState("univicoustic");
  const [search, setSearch] = useState("");
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [erpUploading, setErpUploading] = useState(false);
  const [erpResult, setErpResult] = useState(null);

  const { data: products = [], isLoading } = useQuery({
    queryKey: ["catalog", division, search],
    queryFn: async () => (await api.get("/products", { params: { division_id: division, include_inactive: true, search } })).data,
  });

  const toggle = async (p) => { await api.post(`/products/${p.id}/toggle`); qc.invalidateQueries({ queryKey: ["catalog"] }); };

  const downloadTemplate = async () => {
    const token = localStorage.getItem("sc_token");
    const res = await fetch(`${API}/products/template`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "product_template.xlsx"; a.click();
  };

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/products/bulk-upload?division_id=${division}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.created} products`); qc.invalidateQueries({ queryKey: ["catalog"] });
    } catch (err) { toast.error(apiError(err)); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const downloadErpTemplate = async () => {
    const token = localStorage.getItem("sc_token");
    const res = await fetch(`${API}/products/erp-mapping/template`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "erp_code_mapping_template.csv"; a.click();
  };

  const uploadErp = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setErpUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/products/erp-mapping?division_id=${division}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setErpResult(data);
      toast.success(`Linked ${data.summary.matched} product code(s)`);
      qc.invalidateQueries({ queryKey: ["catalog"] });
    } catch (err) { toast.error(apiError(err)); } finally { setErpUploading(false); if (erpFileRef.current) erpFileRef.current.value = ""; }
  };

  const linkedCount = products.filter((p) => p.erp_product_code).length;
  return (
    <div>
      <PageHeader title="Product Catalog" subtitle="CRM products for quoting plus the full Milestone ERP product-code master.">
        {isAdmin && <>
        <Button variant="outline" className="gap-1.5" onClick={downloadTemplate} data-testid="download-template-btn"><Download className="h-4 w-4" /> Template</Button>
        <Button variant="outline" className="gap-1.5" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="bulk-upload-btn">{uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Bulk Upload</Button>
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={upload} />
        <Button variant="outline" className="gap-1.5 border-brand/40 text-brand hover:bg-brand/5" onClick={() => erpFileRef.current?.click()} disabled={erpUploading} data-testid="erp-import-btn">{erpUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} Import ERP Codes</Button>
        <input ref={erpFileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={uploadErp} />
        <Button className="bg-slate-900 hover:bg-slate-800 gap-1.5" onClick={() => { setEditing(null); setDlgOpen(true); }} data-testid="add-product-btn"><Plus className="h-4 w-4" /> Add Product</Button>
        </>}
      </PageHeader>

      <Tabs defaultValue="catalog">
        <TabsList className="mb-5">
          <TabsTrigger value="catalog" data-testid="products-tab-catalog">CRM Catalog</TabsTrigger>
          <TabsTrigger value="erp" data-testid="products-tab-erp">ERP Product Codes</TabsTrigger>
        </TabsList>
        <TabsContent value="catalog">

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <DivisionTag id="univicoustic" size="md" />
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products…" className="h-9 pl-9" data-testid="product-search" />
        </div>
        <span className="text-sm text-slate-500 ml-auto">{linkedCount}/{products.length} linked to ERP · {products.length} products</span>
      </div>

      <p className="text-xs text-slate-400 mb-4 -mt-1">
        {isAdmin && <>
        <Link2 className="h-3 w-3 inline mr-1 text-brand" />
        Link products to Milestone ERP codes: <button onClick={downloadErpTemplate} className="text-brand underline hover:no-underline" data-testid="erp-template-btn">download the CSV template</button>, fill in <span className="font-mono">name</span> + <span className="font-mono">erp_product_code</span>, then “Import ERP Codes”. Matching is by product name.
        </>}
      </p>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          {isLoading ? <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                {["Product", "Category", "Model", "ERP Code", "Price", "Unit", "Active", ""].map((h) => <TableHead key={h} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>)}
              </TableRow></TableHeader>
              <TableBody>
                {products.map((p) => (
                  <TableRow key={p.id} className={p.is_active ? "" : "opacity-50"} data-testid={`product-row-${p.id}`}>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell className="text-slate-600">{p.category}<span className="text-slate-400">{p.sub_category ? ` · ${p.sub_category}` : ""}</span></TableCell>
                    <TableCell className="font-mono text-xs text-slate-500">{p.model_number || "—"}</TableCell>
                    <TableCell data-testid={`product-erp-${p.id}`}>{p.erp_product_code
                      ? <span className="font-mono text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded">{p.erp_product_code}</span>
                      : <span className="text-xs text-slate-300">unlinked</span>}</TableCell>
                    <TableCell className="font-mono">{formatINR(p.base_price)}</TableCell>
                    <TableCell className="text-slate-500">{p.unit}</TableCell>
                    <TableCell><Switch checked={p.is_active} onCheckedChange={() => toggle(p)} disabled={!isAdmin} data-testid={`product-toggle-${p.id}`} /></TableCell>
                    <TableCell>{isAdmin && <button onClick={() => { setEditing(p); setDlgOpen(true); }} className="text-slate-400 hover:text-slate-800" data-testid={`product-edit-${p.id}`}><Pencil className="h-4 w-4" /></button>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </Card>
        </TabsContent>
        <TabsContent value="erp"><ErpCodesTable /></TabsContent>
      </Tabs>

      <ProductDialog open={dlgOpen} onOpenChange={setDlgOpen} division={division} initial={editing} onSaved={() => qc.invalidateQueries({ queryKey: ["catalog"] })} />

      <Dialog open={!!erpResult} onOpenChange={(o) => !o && setErpResult(null)}>
        <DialogContent data-testid="erp-result-dialog">
          <DialogHeader><DialogTitle className="font-heading">ERP Code Import Result</DialogTitle></DialogHeader>
          {erpResult && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg bg-emerald-50 p-3 text-center"><p className="font-heading text-2xl font-bold text-emerald-600">{erpResult.summary.matched}</p><p className="text-[11px] text-emerald-700 uppercase font-semibold">Linked</p></div>
                <div className="rounded-lg bg-amber-50 p-3 text-center"><p className="font-heading text-2xl font-bold text-amber-600">{erpResult.summary.ambiguous}</p><p className="text-[11px] text-amber-700 uppercase font-semibold">Ambiguous</p></div>
                <div className="rounded-lg bg-red-50 p-3 text-center"><p className="font-heading text-2xl font-bold text-red-600">{erpResult.summary.unmatched}</p><p className="text-[11px] text-red-700 uppercase font-semibold">Unmatched</p></div>
              </div>
              {(erpResult.unmatched?.length > 0 || erpResult.ambiguous?.length > 0) && (
                <div className="max-h-56 overflow-y-auto border rounded-md divide-y">
                  {erpResult.ambiguous?.map((r, i) => (
                    <div key={`a${i}`} className="px-3 py-2 flex justify-between gap-2"><span className="text-slate-700 truncate">{r.name}</span><span className="text-xs text-amber-600 whitespace-nowrap">{r.erp_product_code} · {r.matches} matches</span></div>
                  ))}
                  {erpResult.unmatched?.map((r, i) => (
                    <div key={`u${i}`} className="px-3 py-2 flex justify-between gap-2"><span className="text-slate-700 truncate">{r.name}</span><span className="text-xs text-red-500 whitespace-nowrap">{r.erp_product_code} · no match</span></div>
                  ))}
                </div>
              )}
              <p className="text-xs text-slate-400">Unmatched / ambiguous rows: fix the product name in the CRM (or edit the product to set its code manually), then re-import.</p>
            </div>
          )}
          <DialogFooter><Button onClick={() => setErpResult(null)} data-testid="erp-result-close">Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
